import { Box, Button, Grid, GridSize, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ReactElement, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { ResponsiveStyleValue } from '@mui/system';
import LastOrderBg from '../assets/images/last_order_bg.png';
import { useLastOrderByContractReferenceIdQuery } from '../queries/__generated__/lastOrderByContractReferenceId.generated';
import useCurrencyFormat from '../hooks/useCurrencyFormat';
import AddOrderToBasketButton from '../common/components/AddOrderToBasketButton';
import { SearchContract } from '../search/product/SearchProductHit';
import { DisplayMode } from '../search/state/state';
import { RoutePaths } from '../routes/AppRoutes';
import { longDateFormat } from '../common/state/state';

interface LastOrderRecommendationProps {
  contract: SearchContract;
  sizes?: { [key: string]: GridSize };
  displayMode: DisplayMode;

}

export default function LastOrderRecommendation({ contract, sizes, displayMode }: LastOrderRecommendationProps): React.JSX.Element {
  const { t } = useTranslation(['basket', 'common', 'order', 'products']);
  const navigate = useNavigate();
  const currencyFormat = useCurrencyFormat();
  const { data } = useLastOrderByContractReferenceIdQuery({
    variables: { contractReferenceId: contract.reference_id! },
  });
  const cardSizes = useMemo((): ResponsiveStyleValue<GridSize> => {
    return { xs: 12, sm: 6, md: 6, lg: 3, xl: 4, ...sizes };
  }, [sizes]);

  const recommendationInfo = useMemo((): ReactElement => {
    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {data && data.order[0] && (
          <>
            <Typography variant="headlineSmall">
              {t('order:your_last_order')}
            </Typography>
            <Typography variant="titleMedium">
              {format(data.order[0].created_at, longDateFormat)}
            </Typography>
            <Typography variant="titleSmall" color="tertiary" sx={{ mb: 1.5 }}>
              {currencyFormat.format(data.order[0].total_price_excl_tax!)}
            </Typography>
          </>
        )}
      </>
    );
  }, [data]);

  const CTAButtons = useMemo((): ReactElement => {
    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {data && data.order[0] && (
          <>
            <AddOrderToBasketButton
              orderReferenceId={data.order[0].reference_id}
              color="tertiary"
            />
            <Button
              variant="outlined"
              color="tertiary"
              onClick={() => navigate(generatePath(RoutePaths.ORDER_TRACKING, { orderReferenceId: data.order[0].reference_id }))}
            >
              <Typography color="tertiary">
                {t('basket:see_your_order')}
              </Typography>
            </Button>
          </>
        )}
      </>
    );
  }, [data]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {!!data?.order.length && (
        displayMode === DisplayMode.GRID
          ? (
            <Grid size={cardSizes}>
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  backgroundColor: 'tertiaryContainer.main',
                  backgroundImage: `url(${LastOrderBg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  flexDirection: 'column',
                  borderRadius: 6,
                  p: 2,
                  boxSizing: 'border-box',
                  height: '100%',
                  flex: { xs: 1, sm: 'initial' },
                  gap: 1,
                }}
              >
                <Box sx={{ mb: 0.5 }}>
                  {recommendationInfo}
                </Box>
                {CTAButtons}
              </Box>
            </Grid>
          )
          : (
            <Box
              sx={{
                width: '100%',
                flexDirection: 'row',
                display: 'flex',
                backgroundColor: 'tertiaryContainer.main',
                backgroundImage: `url(${LastOrderBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderRadius: 6,
                p: 2,
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <Box sx={{ flex: 1 }}>
                {recommendationInfo}
              </Box>
              {CTAButtons}
            </Box>

          )
      )}
    </>
  );
}
